import { Box, Typography, useMediaQuery } from "@mui/material";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const Directions = ({ positions, onHandleChange }: { positions: { lat: number, lng: number }[], onHandleChange: (...args: any) => void }) => {
  const map = useMap();
  const routesLibrary = useMapsLibrary('routes');

  const [directionsService, setDirectionsService] = useState<any>();
  const [directionsRenderer, setDirectionsRenderer] = useState<any>();
  const [routes, setRoutes] = useState<any[]>([]);
  const [routeIndex, setRouteIndex] = useState(0);

  const selected = useMemo(() => routes[routeIndex], [routes, routeIndex]);
  const isMobile = useMediaQuery("(max-width:650px)");
  const lastExportedRef = useRef<string>("");

  // Calcula a distância total somando todas as pernas da rota (legs)
  const totalDistance = useMemo(() => {
    if (!selected?.legs) return "";
    const totalMeters = selected.legs.reduce((acc: number, leg: any) => acc + (leg.distance?.value || 0), 0);
    if (totalMeters >= 1000) {
      return `${(totalMeters / 1000).toFixed(1)} km`;
    }
    return `${totalMeters} m`;
  }, [selected]);

  // Calcula a duração total somando todas as pernas da rota (legs)
  const totalDuration = useMemo(() => {
    if (!selected?.legs) return "";
    const totalSeconds = selected.legs.reduce((acc: number, leg: any) => acc + (leg.duration?.value || 0), 0);
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    if (hrs > 0) {
      return `${hrs} h ${mins} min`;
    }
    return `${mins} min`;
  }, [selected]);

  useEffect(() => {
    if (!routesLibrary || !map) return;
    setDirectionsService(new routesLibrary.DirectionsService());
    setDirectionsRenderer(
      new routesLibrary.DirectionsRenderer({
        draggable: true,
        map
      })
    );
  }, [routesLibrary, map]);

  useEffect(() => {
    if (!directionsRenderer) return;
    const g = (window as any).google;
    if (!g || !g.maps) return;

    const listener = directionsRenderer.addListener(
      'directions_changed',
      () => {
        const result = directionsRenderer.getDirections();
        if (result) {
          setRoutes(result.routes);
        }
      }
    );
    return () => {
      if (g.maps.event) g.maps.event.removeListener(listener);
    };
  }, [directionsRenderer]);

  useEffect(() => {
    const g = (window as any).google;

    if (
      !directionsService ||
      !directionsRenderer ||
      !g?.maps ||
      positions.length < 2
    ) {
      return;
    }

    const origin = positions[0];
    const destination = positions[positions.length - 1];

    const waypoints = positions
      .slice(1, -1)
      .map(position => ({
        location: position,
        stopover: true
      }));

    directionsService
      .route({
        origin,
        destination,
        waypoints,
        optimizeWaypoints: false, 
        travelMode: g.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false
      })
      .then((response: any) => {
        directionsRenderer.setDirections(response);
        setRoutes(response.routes);
      })
      .catch((err: any) => {
        console.warn("Directions: routing failed.", err?.message ?? err);
      });

  }, [directionsService, directionsRenderer, positions]);

  useEffect(() => {
    if (!directionsRenderer) return;
    directionsRenderer.setRouteIndex(routeIndex);
  }, [routeIndex, directionsRenderer]);


  const exportGeneratedRoute = useCallback(() => {
    if (!selected?.legs?.length) return;

    const newPositions = [
      selected.legs[0].start_location,
      ...selected.legs.map((leg: any) => leg.end_location)
    ].map((location: any) => ({
      lat: typeof location.lat === "function" ? location.lat() : location.lat,
      lng: typeof location.lng === "function" ? location.lng() : location.lng
    }));

    // Deduplicador: só propaga os dados para o componente pai se houver alteração real de rota
    const signature = JSON.stringify(
      newPositions.map(p => [p.lat.toFixed(6), p.lng.toFixed(6)])
    );
    if (signature === lastExportedRef.current) return;
    lastExportedRef.current = signature;

    onHandleChange(newPositions);
  }, [selected, onHandleChange]);

  useEffect(() => {
    exportGeneratedRoute();
  }, [exportGeneratedRoute]);

  if (!selected || !selected.legs || selected.legs.length === 0) return null;

  return (
    <Box className="directions row text-start">
      <Box style={{ background: "#ffff", padding: '10px', borderRadius: '4px', borderLeft: '4px solid #0FB403', margin: '5px 0' }}>
        {!isMobile && (
          <Box className="col-md-12">
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
              {selected.legs[0].start_address?.split(',')[0]} → {selected.legs[selected.legs.length - 1].end_address?.split(',')[0]}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#666' }}>
                <b>Distância Total:</b> {totalDistance}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>
                <b>Duração Total:</b> {totalDuration}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Directions;